import React, { useState, ReactNode } from 'react';
import { ThemeProvider } from '@mui/material';
import { themeCreator } from './base';
import GlobalStyles from '@mui/material/GlobalStyles';
import { alpha } from '@mui/material/styles';
import { useEffect } from 'react';

export const ThemeContext = React.createContext(
  (themeName: string): void => {}
);

interface ThemeProviderWrapperProps {
  children: ReactNode;
}

const ThemeProviderWrapper: React.FC<ThemeProviderWrapperProps> = (props) => {
  const curThemeName = localStorage.getItem('appTheme') || 'PureDarkTheme';
  const [themeName, _setThemeName] = useState(curThemeName);
  const theme = themeCreator(themeName);
  const setThemeName = (themeName: string): void => {
    localStorage.setItem('appTheme', themeName);
    _setThemeName(themeName);
  };

  // Update meta theme-color to match app bar/background for PWA feel
  useEffect(() => {
    const el = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
    if (el) {
      const color = theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.primary.main;
      el.setAttribute('content', color);
    }
  }, [theme.palette.mode, theme.palette.primary.main, theme.palette.background.default]);

  return (
    <ThemeContext.Provider value={setThemeName}>
      <ThemeProvider theme={theme}>
        {/* Inject a style tag to force the scrollbar to always show */}
        <style>{`body { overflow-y: scroll !important; padding-right: 0 !important; }`}</style>
        <GlobalStyles
          styles={(theme) => ({
            'html, body, #root': {
              height: '100%',
              minHeight: '100%'
            },
            '.MuiBackdrop-root, .search-modal-backdrop': {
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 1300
            },
            '.MuiBackdrop-root:hover, .search-modal-backdrop:hover': {
              cursor: 'pointer'
            },

            // Global action styles for IconButtons (and nested icons)
            '.MuiIconButton-root.action-success': {
              color: `${theme.palette.success.main} !important`,
              '& .MuiSvgIcon-root': { color: 'inherit !important' },
              '&:hover': {
                backgroundColor: alpha(theme.palette.success.main, 0.15)
              }
            },
            '.MuiIconButton-root.action-secondary': {
              color: `${theme.palette.secondary.dark} !important`,
              '& .MuiSvgIcon-root': { color: 'inherit !important' },
              '&:hover': {
                backgroundColor: alpha(theme.palette.secondary.dark, 0.15)
              }
            },
            '.MuiIconButton-root.action-warning': {
              color: `${theme.palette.warning.main} !important`,
              '& .MuiSvgIcon-root': { color: 'inherit !important' },
              '&:hover': {
                backgroundColor: alpha(theme.palette.warning.main, 0.15)
              }
            },
            '.MuiIconButton-root.action-info': {
              color: `${theme.palette.info.main} !important`,
              '& .MuiSvgIcon-root': { color: 'inherit !important' },
              '&:hover': {
                backgroundColor: alpha(theme.palette.info.main, 0.15)
              }
            },
            '.MuiIconButton-root.action-error': {
              color: `${theme.palette.error.main} !important`,
              '& .MuiSvgIcon-root': { color: 'inherit !important' },
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.15)
              }
            },
            '.MuiIconButton-root.action-primary': {
              color: `${theme.palette.primary.main} !important`,
              '& .MuiSvgIcon-root': { color: 'inherit !important' },
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.15)
              }
            },
          })}
        />
          {props.children}
        </ThemeProvider>
      </ThemeContext.Provider>
  );
};

export default ThemeProviderWrapper;
